import { useEffect, useState } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { Gift, Plus, Edit2, Trash2, Calendar, TrendingUp, Users, Search, X, Tag, Percent, DollarSign } from 'lucide-react';
import { AddPromotionModal } from './AddPromotionModal';
import { useOrders } from '../../hooks/useOrders';
import { promotionsService, Promotion as ApiPromotion, CreatePromotionDto, UpdatePromotionDto } from '../../services/promotions.service';
import { toast } from 'sonner';

interface PromotionsPageProps {
  onNavigate: (page: string) => void;
}

type PromotionStatus = 'all' | 'active' | 'scheduled' | 'expired';

interface Promotion {
  id: string;
  title: string;
  code: string;
  type: 'percentage' | 'fixed' | 'bogo';
  value: number;
  description: string;
  startDate: string;
  endDate: string;
  status: 'active' | 'scheduled' | 'expired';
  usageCount: number;
  usageLimit: number;
  minOrderValue?: number;
}

export function PromotionsPage({ onNavigate }: PromotionsPageProps) {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return true;
  });
  const { orders: ordersData } = useOrders({ page: 1, limit: 50 });
  const [filterStatus, setFilterStatus] = useState<PromotionStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(false);

  const toUiType = (type: ApiPromotion['type']): Promotion['type'] => {
    if (type === 'PERCENTAGE') return 'percentage';
    if (type === 'FIXED_AMOUNT') return 'fixed';
    return 'bogo';
  };

  const toApiType = (type: Promotion['type']): ApiPromotion['type'] => {
    if (type === 'percentage') return 'PERCENTAGE';
    if (type === 'fixed') return 'FIXED_AMOUNT';
    return 'BUY_X_GET_Y';
  };

  const deriveUiStatus = (promotion: ApiPromotion): Promotion['status'] => {
    const now = new Date();
    const start = new Date(promotion.startDate);
    const end = new Date(promotion.endDate);

    if (!promotion.isActive || promotion.status === 'EXPIRED' || promotion.status === 'DISABLED' || end < now) {
      return 'expired';
    }
    if (promotion.status === 'SCHEDULED' || promotion.status === 'DRAFT' || start > now) {
      return 'scheduled';
    }
    return 'active';
  };

  const mapApiPromotion = (promotion: ApiPromotion): Promotion => ({
    id: promotion.id,
    title: promotion.name,
    code: promotion.code,
    type: toUiType(promotion.type),
    value: Number(promotion.discountValue ?? 0),
    description: promotion.description || '',
    startDate: promotion.startDate,
    endDate: promotion.endDate,
    status: deriveUiStatus(promotion),
    usageCount: promotion.usageCount || 0,
    usageLimit: promotion.usageLimit || 0,
    minOrderValue: promotion.minPurchase ? Number(promotion.minPurchase) : undefined,
  });

  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const response = await promotionsService.getPromotions();
      const payload: any = response.data;

      const rows: ApiPromotion[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];

      setPromotions(rows.map(mapApiPromotion));
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load promotions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      case 'scheduled':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
      case 'expired':
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'percentage':
        return <Percent className="w-4 h-4" />;
      case 'fixed':
        return <DollarSign className="w-4 h-4" />;
      case 'bogo':
        return <Gift className="w-4 h-4" />;
      default:
        return <Tag className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type: string, value: number) => {
    switch (type) {
      case 'percentage':
        return `${value}% OFF`;
      case 'fixed':
        return `$${value} OFF`;
      case 'bogo':
        return 'BOGO';
      default:
        return '';
    }
  };

  const handleAddPromotion = async (data: any) => {
    const payload: CreatePromotionDto = {
      code: data.code,
      name: data.title,
      description: data.description,
      type: toApiType(data.type),
      discountValue: data.value,
      minPurchase: data.minOrderValue,
      usageLimit: data.usageLimit,
      startDate: data.startDate,
      endDate: data.endDate,
      isActive: true,
    };

    try {
      await promotionsService.createPromotion(payload);
      toast.success('Promotion created');
      setIsAddModalOpen(false);
      await fetchPromotions();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create promotion');
    }
  };

  const handleEditPromotion = async (data: any) => {
    if (!editingPromotion) return;

    const payload: UpdatePromotionDto = {
      code: data.code,
      name: data.title,
      description: data.description,
      type: toApiType(data.type),
      discountValue: data.value,
      minPurchase: data.minOrderValue,
      usageLimit: data.usageLimit,
      startDate: data.startDate,
      endDate: data.endDate,
    };

    try {
      await promotionsService.updatePromotion(editingPromotion.id, payload);
      toast.success('Promotion updated');
      setEditingPromotion(null);
      await fetchPromotions();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update promotion');
    }
  };

  const handleDeletePromotion = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this promotion?')) {
      try {
        await promotionsService.deletePromotion(id);
        toast.success('Promotion deleted');
        await fetchPromotions();
      } catch (error: any) {
        toast.error(error?.message || 'Failed to delete promotion');
      }
    }
  };

  const handleToggleStatus = async (promotion: Promotion) => {
    try {
      const isActivating = promotion.status !== 'active';
      const now = new Date();
      const start = new Date(promotion.startDate);
      const end = new Date(promotion.endDate);

      let nextStatus: UpdatePromotionDto['status'] = 'DISABLED';
      if (isActivating) {
        if (start > now) {
          nextStatus = 'SCHEDULED';
        } else if (end < now) {
          nextStatus = 'EXPIRED';
        } else {
          nextStatus = 'ACTIVE';
        }
      }

      await promotionsService.updatePromotion(promotion.id, {
        isActive: isActivating,
        status: nextStatus,
      });

      toast.success(`Promotion ${isActivating ? 'activated' : 'deactivated'}`);
      await fetchPromotions();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update promotion status');
    }
  };

  const filteredPromotions = promotions.filter(p => {
    const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
    const matchesSearch = p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const stats = {
    total: promotions.length,
    active: promotions.filter(p => p.status === 'active').length,
    scheduled: promotions.filter(p => p.status === 'scheduled').length,
    expired: promotions.filter(p => p.status === 'expired').length,
    totalUsage: promotions.reduce((sum, p) => sum + p.usageCount, 0)
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar 
        activePage="promotions" 
        onNavigate={onNavigate} 
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(!sidebarOpen)}
        ordersCount={ordersData.length}
      />
      
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
      }`}>
        <TopBar 
          onMenuClick={() => setSidebarOpen(!sidebarOpen)} 
          sidebarOpen={sidebarOpen}
          onNavigate={onNavigate}
        />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Promotions & Offers</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Manage discount codes, special offers, and promotional campaigns
                </p>
              </div>
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm font-medium text-sm whitespace-nowrap"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden xs:inline">Create Promotion</span>
                <span className="xs:hidden">Create</span>
              </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Gift className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Total</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Active</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.active}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Scheduled</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.scheduled}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Expired</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.expired}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                    <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Total Uses</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.totalUsage}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 mb-6">
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Filter Buttons */}
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'all', label: 'All Promotions', count: stats.total },
                    { value: 'active', label: 'Active', count: stats.active },
                    { value: 'scheduled', label: 'Scheduled', count: stats.scheduled },
                    { value: 'expired', label: 'Expired', count: stats.expired }
                  ].map((filter) => (
                    <button
                      key={filter.value}
                      onClick={() => setFilterStatus(filter.value as PromotionStatus)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        filterStatus === filter.value
                          ? 'bg-indigo-600 text-white shadow-md'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {filter.label}
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        filterStatus === filter.value
                          ? 'bg-white/20 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}>
                        {filter.count}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Search */}
                <div className="relative flex-1 lg:max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search promotions..."
                    className="w-full pl-10 pr-4 py-2 text-sm bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Promotions List */}
            {filteredPromotions.length === 0 ? (
              <div className="bg-white dark:bg-gray-900 rounded-xl p-12 text-center border border-gray-200 dark:border-gray-800">
                <Gift className="w-16 h-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  No promotions found
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {searchQuery || filterStatus !== 'all'
                    ? 'Try adjusting your filters or search query'
                    : 'Get started by creating your first promotion'
                  }
                </p>
                {!searchQuery && filterStatus === 'all' && (
                  <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                  >
                    <Plus className="w-5 h-5" />
                    Create Promotion
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredPromotions.map((promotion) => (
                  <div
                    key={promotion.id}
                    className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-800 hover:shadow-lg transition-all"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                          {getTypeIcon(promotion.type)}
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                            {promotion.title}
                          </h3>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getStatusBadge(promotion.status)}`}>
                              {promotion.status.toUpperCase()}
                            </span>
                            <span className="text-xs px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full font-semibold">
                              {getTypeLabel(promotion.type, promotion.value)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditingPromotion(promotion)}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePromotion(promotion.id)}
                          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {promotion.description}
                    </p>

                    {/* Code */}
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Promo Code</p>
                          <p className="text-sm font-mono font-bold text-gray-900 dark:text-white">
                            {promotion.code}
                          </p>
                        </div>
                        <button className="text-xs px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors font-medium">
                          Copy
                        </button>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Valid Period</p>
                        <p className="text-xs font-medium text-gray-900 dark:text-white">
                          {new Date(promotion.startDate).toLocaleDateString()} - {new Date(promotion.endDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Min. Order</p>
                        <p className="text-xs font-medium text-gray-900 dark:text-white">
                          ${promotion.minOrderValue || 0}
                        </p>
                      </div>
                    </div>

                    {/* Usage Progress */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-xs mb-2">
                        <span className="text-gray-600 dark:text-gray-400">Usage</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {promotion.usageCount} / {promotion.usageLimit}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-indigo-600 h-2 rounded-full transition-all"
                          style={{ width: `${(promotion.usageCount / promotion.usageLimit) * 100}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Actions */}
                    {promotion.status !== 'expired' && (
                      <button
                        onClick={() => handleToggleStatus(promotion)}
                        className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
                          promotion.status === 'active'
                            ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                            : 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30'
                        }`}
                      >
                        {promotion.status === 'active' ? 'Deactivate' : 'Activate'} Promotion
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Add/Edit Promotion Modal */}
      {(isAddModalOpen || editingPromotion) && (
        <AddPromotionModal
          promotion={editingPromotion}
          onClose={() => {
            setIsAddModalOpen(false);
            setEditingPromotion(null);
          }}
          onSave={editingPromotion ? handleEditPromotion : handleAddPromotion}
        />
      )}
    </div>
  );
}